import React from 'react';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

const PrivacyPolicy = () => {
  useDocumentMetadata(
    'Privacy Policy - Dakhla',
    'Read Dakhla\'s Privacy Policy to understand how we handle student data, application privacy, and security in Pakistan.'
  );

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200/80 p-8 sm:p-12">
        {/* Header */}
        <div className="border-b border-slate-100 pb-8 mb-8">
          <div className="text-sm font-bold uppercase tracking-wider text-primary-600 mb-2">
            Legal & Compliance
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Privacy Policy
          </h1>
          <p className="mt-2 text-slate-500 text-sm">
            Last Updated: July 2026 • Applies to all Dakhla users and services in Pakistan.
          </p>
        </div>

        {/* Policy Content */}
        <div className="prose prose-slate max-w-none space-y-8 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">
              1. Information We Collect
            </h2>
            <p>
              Dakhla collects personal and academic information necessary to facilitate your higher education search and university application submission process. This includes:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Account Credentials:</strong> Name, email address, phone number, and password when you register.</li>
              <li><strong>Academic Profile:</strong> Matric/O-Level, Intermediate/A-Level grades, test scores (MDCAT, ECAT, NTS, NAT, etc.), and intended fields of study.</li>
              <li><strong>Application Data:</strong> Information submitted when applying directly to partner universities through our portal.</li>
              <li><strong>Technical Data:</strong> IP address, browser type, and usage analytics to improve platform performance.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">
              2. How We Use Your Data
            </h2>
            <p>
              Your data is used strictly to empower your university search and application process:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>To match you with eligible degree programs and university admission deadlines.</li>
              <li>To transmit your application directly to designated Partner Universities upon your explicit submission.</li>
              <li>To communicate important admission updates, deadline reminders, and support responses.</li>
              <li>To prevent fraudulent access and maintain platform security.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">
              3. Data Sharing & Third-Party Protection
            </h2>
            <p>
              Dakhla respects student privacy and strictly adheres to Pakistani data protection principles:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Partner Universities:</strong> We share applicant details only with partner institutions that a student explicitly chooses to apply to.</li>
              <li><strong>No Selling of Data:</strong> Dakhla does NOT sell or rent personal student data to third-party advertisers or telemarketers.</li>
              <li><strong>Legal Compliance:</strong> Information may be disclosed if required by law or valid legal process in the Islamic Republic of Pakistan.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">
              4. Data Retention & Security
            </h2>
            <p>
              We implement industry-standard encryption, secure servers, and access controls to safeguard your data. Personal records are retained only as long as necessary to provide our educational services or fulfill legal requirements.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">
              5. Contact & Data Access
            </h2>
            <p>
              You have the right to review, update, or request the deletion of your account data. For privacy inquiries or data requests, please contact our support desk at <a href="mailto:support.dakhla@gmail.com" className="text-primary-600 font-semibold hover:underline">support.dakhla@gmail.com</a> or via our Contact Page.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
